import { useZero } from "@rocicorp/zero/react";
import { useMemo, useState } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import { useGroupMemberships } from "../../../hooks/useGroupMemberships";
import { useHasAccess } from "../../../hooks/useHasAccess";
import { useIdpSyncLock } from "../../../hooks/useIdpSyncLock";
import { useQuery } from "../../../hooks/useQuery";
import { useUrlSort } from "../../../hooks/useUrlSort";
import { IdpLockTooltip } from "../../IdpLockTooltip";
import { useResolveScopeName } from "../../../hooks/useResolveScopeName";
import {
  makeUrl,
  navigate,
  Route,
  routes,
  useCurrentUrl,
} from "../../../lib/urls";
import { Button } from "../../Button";
import { ButtonLink } from "../../Button/ButtonLink";
import { ConfirmModal } from "../../ConfirmModal";
import { Dropdown } from "../../Dropdown";
import { EmptyState } from "../../EmptyState";
import { GroupMemberFacePile } from "../../GroupMemberFacePile";
import { Icon } from "../../Icon";
import { ListTable } from "../../ListTable";
import { Text } from "../../Text";
import { RoleBadgeList } from "../RoleBadgeList";
import {
  BuiltinLockBadge,
  ColoredIconBadge,
  Container,
  Header,
  HeaderCountBadge,
  HoverActions,
  RowNameContent,
  RowNameLayout,
  RowNameLine,
  ScrollArea,
  SearchField,
} from "../shared";
import { CreateUserGroupModal } from "./CreateUserGroupModal";
import { buildDisplayRows, filterRows, sortRows } from "./lib";

const isActive = (m: { user?: { deactivated: boolean | null } | null }) =>
  !m.user?.deactivated;

export function UserGroupsSettings() {
  const z = useZero();

  const [groups, groupsResult] = useQuery(queries.allUserGroups());
  const [adminGrants] = useQuery(queries.tenantAdminGrants());
  const [customRoles] = useQuery(queries.allRolesLite());
  const { resolveScopeName, personalSpaceIds } = useResolveScopeName();
  const { memberCounts } = useGroupMemberships();

  const canManageUsers = useHasAccess("tenant:users_manage");
  const canCreateGroup = useHasAccess("tenant:user_group_create");
  const { locked: idpSyncLocked, reason: idpSyncReason } =
    useIdpSyncLock("groupLifecycle");

  const { q } = useCurrentUrl(routes.settings.userGroups);
  const search = q ?? "";
  const setSearch = (next: string) => {
    navigate(null, { q: next }, { replace: true });
  };

  const [confirmDeleteGroup, setConfirmDeleteGroup] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const { sortColumn, sortDirection, handleSort, sortableColumns } = useUrlSort(
    routes.settings.userGroups,
    {
      name: "asc",
      members: "asc",
      roles: "asc",
    }
  );

  const fullAccessRoleIds = useMemo(
    () =>
      new Set(
        customRoles
          .filter((r) =>
            r.rolePermissions.some((rp) => rp.permission === "allow_all")
          )
          .map((r) => r.id)
      ),
    [customRoles]
  );

  const rolesMap = useMemo(
    () =>
      new Map(
        customRoles.map((r) => [
          r.id,
          { id: r.id, name: r.name, scopeKind: r.scopeKind },
        ])
      ),
    [customRoles]
  );

  const displayRows = useMemo(
    () =>
      buildDisplayRows(
        groups,
        adminGrants,
        memberCounts,
        resolveScopeName,
        fullAccessRoleIds,
        rolesMap,
        isActive,
        personalSpaceIds
      ),
    [
      groups,
      adminGrants,
      memberCounts,
      resolveScopeName,
      fullAccessRoleIds,
      rolesMap,
      personalSpaceIds,
    ]
  );

  const filteredRows = useMemo(
    () => filterRows(displayRows, search),
    [displayRows, search]
  );

  const sortedRows = useMemo(
    () => sortRows(filteredRows, sortColumn, sortDirection),
    [filteredRows, sortColumn, sortDirection]
  );

  const items = useMemo(() => {
    return sortedRows.map((row) => ({
      key: row.id,
      ...(row.isBuiltin
        ? {
            link: canManageUsers ? makeUrl(routes.modal.admins) : undefined,
          }
        : {
            link: makeUrl(routes.modal.group, { id: row.id }),
          }),
      name: (
        <RowNameLayout>
          <ColoredIconBadge id={row.id} iconName={row.iconName} />
          <RowNameContent>
            <RowNameLine>
              <Text
                ellipsis
                variant="text-regular-md"
                color="var(--text-primary)"
              >
                {row.name}
              </Text>
              {row.isBuiltin && <BuiltinLockBadge />}
            </RowNameLine>
            <Text variant="text-small" color="var(--text-secondary)">
              {row.memberCount} {row.memberCount === 1 ? "member" : "members"}
            </Text>
          </RowNameContent>
        </RowNameLayout>
      ),
      members:
        row.members.length > 0 ? (
          <GroupMemberFacePile
            members={row.members}
            totalCount={row.memberCount}
            size={24}
            onClickMember={(userId) =>
              navigate(routes.modal.member, { id: userId })
            }
          />
        ) : (
          <Text variant="text-small" color="var(--text-tertiary)">
            None
          </Text>
        ),
      roles: <RoleBadgeList chips={row.roleBadges} />,
      actions: (hasMouse: boolean) =>
        !row.isBuiltin && canCreateGroup ? (
          <HoverActions visible={hasMouse}>
            <Dropdown
              opener={<Button variant="secondary" icon="more" />}
              align="bottom"
              minWidth={160}
              sections={[
                {
                  items: [
                    {
                      type: "button" as const,
                      left: <Icon name="trash" size={16} />,
                      label: "Delete group",
                      disabled: idpSyncLocked,
                      title: idpSyncReason ?? undefined,
                      onClick: () => {
                        setConfirmDeleteGroup({ id: row.id, name: row.name });
                      },
                    },
                  ],
                },
              ]}
            />
          </HoverActions>
        ) : null,
    }));
  }, [
    sortedRows,
    canCreateGroup,
    canManageUsers,
    idpSyncLocked,
    idpSyncReason,
  ]);

  return (
    <Container>
      <Header style={{ paddingRight: 12 }}>
        <Text variant="text-regular-md">Groups</Text>
        <HeaderCountBadge count={filteredRows.length} />
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <SearchField
            placeholder="Search groups…"
            value={search}
            onChange={setSearch}
          />
          {canCreateGroup && (
            <IdpLockTooltip reason={idpSyncLocked ? idpSyncReason : null}>
              {idpSyncLocked ? (
                <Button size="l" icon="plus" disabled />
              ) : (
                <ButtonLink
                  size="l"
                  icon="plus"
                  to={makeUrl(routes.settings.userGroups.new)}
                />
              )}
            </IdpLockTooltip>
          )}
        </div>
      </Header>
      <ScrollArea>
        <ListTable
          headers={{
            name: "Name",
            roles: "Roles",
            members: "Members",
            actions: "",
          }}
          columns={{ roles: "flex", members: "members" }}
          sortableColumns={sortableColumns}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={handleSort}
          items={items}
          emptyState={
            groupsResult.type === "complete" ? (
              <EmptyState
                iconName="shield-half"
                title="No groups"
                description="Try a different search."
              />
            ) : null
          }
        />
      </ScrollArea>
      <Route path={routes.settings.userGroups.new}>
        {() => (
          <CreateUserGroupModal
            open
            onClose={() => navigate(routes.settings.userGroups)}
          />
        )}
      </Route>
      <ConfirmModal
        open={confirmDeleteGroup !== null}
        onClose={() => setConfirmDeleteGroup(null)}
        onConfirm={() => {
          if (confirmDeleteGroup) {
            z.mutate(
              sharedMutators.deleteUserGroup({
                userGroupId: confirmDeleteGroup.id,
              })
            );
          }
          setConfirmDeleteGroup(null);
        }}
        title="Delete group"
        description={
          confirmDeleteGroup
            ? `“${confirmDeleteGroup.name}” will be permanently deleted, along with all of its role grants.`
            : ""
        }
        confirmLabel="Delete"
        destructive
      />
    </Container>
  );
}
